import * as React from "react";
import { cn } from "./cn";
import { Container } from "./container";

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Small kicker above the title. */
  eyebrow?: React.ReactNode;
  /** Page title (rendered as <h1>). */
  title: React.ReactNode;
  /** Supporting description under the title. */
  description?: React.ReactNode;
  /** Optional actions (buttons / links) aligned to the end on wide screens. */
  actions?: React.ReactNode;
  /** Container width. @default "default" */
  width?: React.ComponentProps<typeof Container>["width"];
  /**
   * Keep the actions beside the title on PHONES instead of stacking them below
   * the description. Only for short actions (a single button) — anything wide,
   * an image say, squeezes the heading on a narrow screen. Desktop is
   * unchanged either way. @default false
   */
  inlineActions?: boolean;
}

/**
 * Standard inner-page heading band (About, FAQ, Contact, admin, …). Lighter
 * than the home Hero — keeps every secondary page visually consistent.
 */
export const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  (
    { className, eyebrow, title, description, actions, width = "default", inlineActions, ...props },
    ref,
  ) => {
    const heading = (
      <>
        {eyebrow && (
          <p className="mb-2 text-sm font-bold uppercase tracking-wide text-primary">
            {eyebrow}
          </p>
        )}
        <h1 className="font-heading text-3xl font-extrabold leading-tight text-ink sm:text-4xl">
          {title}
        </h1>
      </>
    );
    const body = description && (
      <p className="mt-4 text-lg leading-relaxed text-muted">{description}</p>
    );

    return (
      <header
        ref={ref}
        className={cn("border-b border-border bg-card", className)}
        {...props}
      >
        {inlineActions ? (
          /* Two columns at every width: the actions sit level with the bottom
             of the heading on phones, and with the bottom of the whole block
             (as in the default layout) from md up. */
          <Container
            width={width}
            className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-6 py-12 sm:py-16"
          >
            <div className="col-start-1 row-start-1 max-w-2xl">{heading}</div>
            {/* Full width under the button on phones, back in the text column
                from md up so it never runs under the actions. */}
            {body && (
              <div className="col-span-2 col-start-1 row-start-2 max-w-2xl md:col-span-1">
                {body}
              </div>
            )}
            {actions && (
              <div className="col-start-2 row-start-1 flex flex-wrap justify-end gap-3 self-end md:row-span-2">
                {actions}
              </div>
            )}
          </Container>
        ) : (
          <Container
            width={width}
            className="flex flex-col gap-6 py-12 sm:py-16 md:flex-row md:items-end md:justify-between"
          >
            <div className="max-w-2xl">
              {heading}
              {body}
            </div>
            {actions && <div className="flex flex-wrap gap-3 md:shrink-0">{actions}</div>}
          </Container>
        )}
      </header>
    );
  },
);
PageHeader.displayName = "PageHeader";
